import { getIngredientText } from '@cf-shared/data-model/food';
import { Log_Meal } from '@cf-shared/data-model/log-meal';

/** what `FoodDb.generateMealTextAsync` joins a meal's food names with for its description */
export const MEAL_DESCRIPTION_SEPARATOR = '; ';

/**
 * The names a recently logged meal can be searched by, besides its own row name (#5246).
 *
 * A recent-meal row on the Add Foods page is named after its slot and day ("Dinner, 5 days ago"),
 * and its description line lists the foods in it, or the recipe it was made from. Searching only
 * the row name meant typing "chicken tacos" never found the meal that held them. These names are
 * handed to the search as the row's alternative names, and rank exactly as any other food's
 * alternative names do (`rankFoodHeaders` in food-search-ranking.ts).
 *
 * Every food in the meal counts, including a Log Macros Manually entry, which has a name but no
 * food behind it. `shownAs` is the description the row shows; it is added when it names something
 * the foods do not, which is the recipe a meal was logged from.
 *
 * The meal is a stored document that older versions of the app wrote, so a missing ingredient
 * list or a name that is not text is skipped rather than trusted.
 */
export function recentMealSearchNames(meal: Log_Meal, shownAs?: string): string[] {
  const names: string[] = [];
  const seen = new Set<string>();
  const add = (name: unknown) => {
    if (typeof name !== 'string') return;
    const trimmed = name.trim();
    const key = trimmed.toLowerCase();
    if (!trimmed || seen.has(key)) return;
    seen.add(key);
    names.push(trimmed);
  };

  const stored: unknown[] = Array.isArray(meal?.ingredients) ? meal.ingredients : [];
  const ingredients = stored.filter((ingredient): ingredient is { name: string } =>
    typeof (ingredient as { name?: unknown } | null)?.name === 'string');
  for (const ingredient of ingredients) {
    add(ingredient.name);
  }
  // the description is either the food list (built exactly like this) or the recipe name. Only the
  // recipe name is added: the food list would repeat every food as one long name, letting words
  // from two different foods answer one search together
  if (typeof shownAs === 'string' && shownAs !== getIngredientText(ingredients, MEAL_DESCRIPTION_SEPARATOR)) {
    add(shownAs);
  }
  return names;
}
